import React, { useEffect, useState } from "react";
import { Image, Paper, Text } from "@mantine/core";
import QRCode from "qrcode";

interface CertificateProps {
  name: string;
  eventType: string;
  eventName: string; // Changed from webinarr to eventName
  date?: string; // Optional for Bootcamp
  startDate?: string; // Optional for Webinar
  endDate?: string; // Optional for Webinar
  completionDate?: string; // Added completionDate prop
  certificateNumber?: string;
  cpeCount?: string;
  cpeHours?: string;
}

const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

export const CertificateGenerator: React.FC<CertificateProps> = ({
  name,
  eventType,
  eventName, // Changed from webinarr to eventName
  date,
  startDate,
  endDate,
  completionDate, // Added completionDate
  certificateNumber,
  cpeCount,
  cpeHours,
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");

  const formattedDate = date ? formatDate(date) : null;
  const formattedStartDate = startDate ? formatDate(startDate) : null;
  const formattedEndDate = endDate ? formatDate(endDate) : null;
  const formattedCompletionDate = completionDate ? formatDate(completionDate) : null;

  console.log('formattedStartDate Check :', formattedStartDate);
  console.log('formattedEndDate:', formattedEndDate);

  const generateQRCode = async (certificateNumber: string): Promise<string> => {
    try {
      const verificationUrl = `https://thinkcloudly.com/cert-verification?cert=${certificateNumber}`;
      const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
        width: 100,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return qrCodeDataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return '';
    }
  };

  useEffect(() => {
    if (certificateNumber) {
      generateQRCode(certificateNumber).then(setQrCodeDataUrl);
    }
  }, [certificateNumber]);

  const imageSrc = eventType === "BOOTCAMP"
    ? "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627"
    : "https://ik.imagekit.io/2fljt9khj/2.png?updatedAt=1749653082627";

  return (
    <Paper style={{ width: "210mm", height: "147mm", position: "relative" }}>
      {/* Background Image */}
      <Image
        src={imageSrc}
        alt="Certificate Background"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />

      {/* Content */}
      <div
        style={{
          color: "#221E45",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          zIndex: 1,
        }}
      >
        {(eventType === 'BOOTAMP-CPE' || eventType === 'CAPSULE COURSE') ? (
          <>
            {/* Name in place of the "THIS CERTIFICATE..." line, styled per request */}
            <Text style={{ fontSize: 30, fontWeight: 700, whiteSpace: "nowrap", marginBottom: 12, fontFamily: "Helvetica, Arial, sans-serif", color: "#FC7A69" }}>
              {name}
            </Text>

            {/* Attended line with Vietinum Pro font and size 19 */}
            <Text style={{ fontSize: 19, whiteSpace: "nowrap", marginBottom: 10, fontFamily: "Vietinum Pro, Georgia, serif" }}>
              has attended the {eventType === 'CAPSULE COURSE' ? 'Masterclass' : 'Bootcamp'} on

            </Text>

            {/* Topic (event name) with font size 23 */}
            <Text style={{ fontSize: 23, fontWeight: 700, whiteSpace: "nowrap", marginBottom: 10, fontFamily: "Helvetica, Arial, sans-serif" }}>
              {eventName}
            </Text>

            {/* CPE line */}
            <Text style={{ fontSize: 14, whiteSpace: "nowrap", marginBottom: 8 }}>
              And is entitled with {cpeCount || '0'} CPE for {cpeHours || '0'} of Training
            </Text>

            {/* Held line */}
            <Text style={{ fontSize: 13, whiteSpace: "nowrap", marginTop: 6 }}>
              {`Held From ${formattedStartDate} To ${formattedCompletionDate}.`}
            </Text>
          </>
        ) : (
          <>
            <Text style={{ fontSize: "20px", whiteSpace: "nowrap", marginBottom: "15px" }}>
              THIS CERTIFICATE IS PROUDLY PRESENTED TO
            </Text>
            <Text style={{
              fontSize: "25px",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              marginBottom: "15px",
              fontFamily: "ui-monospace"
            }}>
              {name}
            </Text>
            <Text style={{
              fontSize: "15px",
              whiteSpace: "nowrap",
              marginBottom: "9px",
              fontFamily: "Garamond",
              textTransform: "uppercase"
            }}>
              TO BE A PART OF THE {eventType} ON<br /> <b>{eventName}</b>
            </Text>
            <Text
              style={{
                fontSize: "15px",
                fontWeight: "520",
                whiteSpace: "nowrap",
                fontFamily: "Georgia",
                textTransform: "uppercase",
              }}
            >
              {eventType === "BOOTCAMP"
                ? `Held From ${formattedStartDate} To ${formattedCompletionDate}.`
                : `Held On ${formattedCompletionDate}.`
              }
            </Text>
          </>
        )}
      </div>

      {/* QR Code */}
      {qrCodeDataUrl && certificateNumber && (
        <div
          style={{
            position: "absolute",
            bottom: "55px",
            left: "75px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2,
          }}
        >
          <img
            src={qrCodeDataUrl}
            alt="QR Code"
            style={{
              width: "90px",
              height: "90px",
              border: "1px solid #ccc",
            }}
          />
        </div>
      )}

      {/* Certificate Number and Issue Date */}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "28px",
          display: "flex",
          flexDirection: "column",
          // gap: "2px",
          zIndex: 2,
        }}
      >
        {(formattedCompletionDate || formattedEndDate || formattedDate) && (
          <Text
            style={{
              fontSize: "10px",
              whiteSpace: "nowrap",
              fontWeight: "normal",
              marginLeft: "40px",
              color: "rgb(51, 51, 51)",
            }}
          >
            Issued On: {formattedCompletionDate || formattedEndDate || formattedDate}
          </Text>
        )}
        {certificateNumber && (
          <Text
            style={{
              fontSize: "11px",
              fontWeight: "400",
              color: "rgb(51, 51, 51)",
            }}
          >
            Certificate No: {certificateNumber}
          </Text>
        )}
      </div>
    </Paper>
  );
};